import { ChordLib } from '@tunasong/music-lib'
import type { Chord, NoteEvent } from '@tunasong/music-lib'
import type { NoteName } from '@tunasong/schemas'
import { useEffect, useRef } from 'react'
import { usePlayNotes } from '../player/piano-player.hook.js'

/**
 * Reharm Lab v1 N-chord audition playback. Each chord plays as a half-note,
 * separated by GAP_MS, supporting N = 4 (no insertion) or N = 5 (secondary-
 * dominant insertion active). One-shot — `play` cancels any in-flight audition
 * and starts fresh; `stop` cancels in-flight; auto-cancel on unmount.
 *
 * Generalises the cadence-lab-playback pattern. v1.5 follow-up: promote
 * `chordToEvents` to a shared `player/` helper once a fourth caller appears.
 *
 * Spec: docs/superpowers/specs/2026-05-19-reharm-lab-design.md §4 (playback)
 */

const TEMPO_BPM = 100
const BEAT_MS = 60_000 / TEMPO_BPM      // 600 ms
const HALF_NOTE_MS = BEAT_MS * 2         // 1200 ms — each chord plays as a half-note
const GAP_MS = 200                       // brief release between chord events
const STEP_MS = HALF_NOTE_MS + GAP_MS    // 1400 ms total per chord-slot
const CHORD_OCTAVE = 4

export interface ReharmStab {
  events: NoteEvent[]
  delayMs: number
}

const chordToEvents = (chord: Chord): NoteEvent[] =>
  ChordLib.getNotes(chord).map(n => ({
    type: 'note' as const,
    note: `${n}${CHORD_OCTAVE}` as NoteName,
    duration: '2n',
  }))

/** Build the N-chord audition — each chord plays as a half-note, separated
 *  by GAP_MS. Pure function: deterministic given the chord list. */
export const buildReharmAuditionStabs = (chords: readonly Chord[]): ReharmStab[] =>
  chords.map((chord, i) => ({
    events: chordToEvents(chord),
    delayMs: i * STEP_MS,
  }))

export interface UseReharmLabPlaybackProps {
  chords: readonly Chord[]
}

export interface ReharmLabPlayback {
  play: () => void
  stop: () => void
}

export const useReharmLabPlayback = (props: UseReharmLabPlaybackProps): ReharmLabPlayback => {
  const { chords } = props
  const { playNotes } = usePlayNotes()
  const timeoutsRef = useRef<number[]>([])

  const stop = (): void => {
    timeoutsRef.current.forEach(t => window.clearTimeout(t))
    timeoutsRef.current = []
  }

  const play = (): void => {
    stop()
    const stabs = buildReharmAuditionStabs(chords)
    for (const stab of stabs) {
      const handle = window.setTimeout(() => playNotes(stab.events), stab.delayMs)
      timeoutsRef.current.push(handle)
    }
  }

  useEffect(() => stop, [])

  return { play, stop }
}
